// OpenStreetMap-based geocoding (Nominatim) and routing (OSRM). No API key.

export async function geocode(query) {
  if (!query || query.trim().length < 3) return [];
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=6&countrycodes=cl&addressdetails=1&q=${encodeURIComponent(query)}`;
  try {
    const res = await fetch(url, { headers: { "Accept-Language": "es" } });
    if (!res.ok) return [];
    const data = await res.json();
    return data.map((d) => ({
      label: shortLabel(d),
      full: d.display_name,
      lat: parseFloat(d.lat),
      lng: parseFloat(d.lon),
      hasNumber: !!(d.address && d.address.house_number),
    }));
  } catch {
    return [];
  }
}

// Reordena "Calle 1012" -> "1012 Calle" (Nominatim estructurado espera el número primero).
function normalizeStreet(text) {
  const t = (text || "").trim();
  const m = t.match(/^(.*?)[,\s]+(\d{1,6})\s*$/);
  if (m) return `${m[2]} ${m[1].trim()}`;
  return t;
}

// Búsqueda estructurada: resuelve mejor el número de la calle.
export async function geocodeStructured(street, city, state) {
  if (!street || street.trim().length < 3) return [];
  const params = new URLSearchParams({ format: "json", addressdetails: "1", limit: "8", countrycodes: "cl", street: normalizeStreet(street), country: "Chile" });
  if (city) params.set("city", city);
  if (state) params.set("state", state);
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, { headers: { "Accept-Language": "es" } });
    if (!res.ok) throw new Error("nominatim");
    const data = await res.json();
    const results = data.map((d) => ({
      label: shortLabel(d),
      full: d.display_name,
      lat: parseFloat(d.lat),
      lng: parseFloat(d.lon),
      hasNumber: !!(d.address && d.address.house_number),
    }));
    // Prioriza resultados con número de calle.
    results.sort((a, b) => (b.hasNumber ? 1 : 0) - (a.hasNumber ? 1 : 0));
    if (results.length === 0) {
      return geocode([normalizeStreet(street), city, state, "Chile"].filter(Boolean).join(", "));
    }
    return results;
  } catch {
    return geocode([normalizeStreet(street), city, state, "Chile"].filter(Boolean).join(", "));
  }
}

function shortLabel(d) {
  const a = d.address || {};
  const parts = [
    a.road ? `${a.road}${a.house_number ? " " + a.house_number : ""}` : null,
    a.suburb || a.neighbourhood || a.village,
    a.city || a.town || a.municipality || a.county,
  ].filter(Boolean);
  return parts.slice(0, 3).join(", ") || d.display_name.split(",").slice(0, 3).join(",");
}
export async function getRoute(from, to) {
  if (!from || !to || from.lat == null || to.lat == null) return null;
  const url = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("osrm");
    const data = await res.json();
    if (!data.routes || !data.routes.length) throw new Error("no route");
    const r = data.routes[0];
    return {
      distanceKm: +(r.distance / 1000).toFixed(1),
      durationMin: Math.round(r.duration / 60),
      geometry: r.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
    };
  } catch {
    const km = +haversine(from, to).toFixed(1);
    return { distanceKm: km, durationMin: Math.round((km / 60) * 60), geometry: [[from.lat, from.lng], [to.lat, to.lng]], estimated: true };
  }
}

export function haversine(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function formatDuration(min) {
  if (min == null) return "—";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}
